<template>
  <custom-dialog
    :is-dialog-open="isDialogOpen"
    dialog-title="Agendamento selecionado"
    max-width="800px"
    :close-dialog="closeDialog"
  >
    <evento-selecionado-comentario-dialog
      :is-dialog-open.sync="isComentarioDialogOpen"
      :ticket-agendamento-id="eventoSelecionado.ticketAgendamentoId"
      @refetch-data="getComentarios"
    />

    <v-form class="multi-col-validation">
      <v-row>
        <v-col
          sm="6"
          cols="12"
        >
          <v-text-field
            :value="eventoSelecionado.ticketProtocolo"
            dense
            outlined
            label="Protocolo do ticket"
            filled
            readonly
          />
        </v-col>

        <v-col
          sm="6"
          cols="12"
        >
          <v-text-field
            :value="eventoSelecionado.solicitanteNome"
            dense
            outlined
            label="Solicitante"
            filled
            readonly
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col
          sm="6"
          cols="12"
        >
          <v-text-field
            :value="eventoSelecionado.solicitanteEmail"
            dense
            outlined
            label="Solicitante email"
            filled
            readonly
          />
        </v-col>

        <v-col
          sm="6"
          cols="12"
        >
          <v-text-field
            :value="eventoSelecionado.solicitanteCelular"
            dense
            outlined
            label="Solicitante celular"
            filled
            readonly
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-text-field
            v-model="eventoSelecionado.tipoAgendamentoNome"
            dense
            outlined
            label="Tipo de agendamento"
            filled
            readonly
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col
          sm="4"
          cols="6"
        >
          <date-picker
            :date-iso="dataAgendamento"
            label="Data"
            readonly
          />
        </v-col>

        <v-col
          sm="4"
          cols="12"
        >
          <time-picker
            :time-iso="horarioAgendamento"
            label="Horário inicial"
            readonly
          />
        </v-col>

        <v-col
          sm="4"
          cols="12"
        >
          <time-picker
            :time-iso="horarioFinalAgendamento"
            label="Horário final"
            readonly
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <endereco-form-fields
            v-if="eventoSelecionado.enderecoVisita"
            readonly
            filled
            :endereco-data="eventoSelecionado.enderecoVisita"
          />
        </v-col>
      </v-row>

      <v-row v-if="timelineComentarios.length">
        <v-expand-transition>
          <v-col cols="12">
            <evento-selecionado-timeline
              :ticket-agendamento-id="eventoSelecionado.ticketAgendamentoId"
              :timeline-comentarios="timelineComentarios"
              @refetch-data="getComentarios"
            />
          </v-col>
        </v-expand-transition>
      </v-row>

      <v-row>
        <v-col
          cols="12"
          class="d-flex justify-center"
        >
          <v-btn
            color="secondary"
            outlined
            class="mr-4"
            @click="closeDialog"
          >
            Cancelar
          </v-btn>

          <v-btn
            color="primary"
            @click="isComentarioDialogOpen = !isComentarioDialogOpen"
          >
            <v-icon left>
              {{ icons.mdiComment }}
            </v-icon>

            Adicionar comentário
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </custom-dialog>
</template>

<script>
import { onMounted, ref, watch } from '@vue/composition-api'
import { mdiComment } from '@mdi/js'
import CustomDialog from '@/components/dialog/Dialog.vue'
import EventoSelecionadoComentarioDialog from './EventoSelecionadoComentarioDialog.vue'
import DatePicker from '@/components/date-picker/DatePicker.vue'
import TimePicker from '@/components/time-picker/TimePicker.vue'
import EnderecoFormFields from '@/components/endereco/EnderecoFormFields.vue'
import EventoSelecionadoTimeline from './EventoSelecionadoTimeline.vue'
import TicketService from '@/services/modules/TicketService'
import useDialog from '@/composables/useDialog'
import { getTimeStringComApenasHorasEMinutos } from '@/utils/dateTime'

export default {
  emits: ['update:is-dialog-open'],

  components: {
    CustomDialog,
    EventoSelecionadoComentarioDialog,
    DatePicker,
    TimePicker,
    EnderecoFormFields,
    EventoSelecionadoTimeline,
  },

  props: {
    isDialogOpen: {
      type: Boolean,
      required: true,
    },
    eventoSelecionado: {
      type: Object,
      required: true,
    },
  },

  setup(props, { emit }) {
    const { closeDialog } = useDialog(emit, 'is-dialog-open')

    const dataAgendamento = ref(null)
    const horarioAgendamento = ref(null)
    const horarioFinalAgendamento = ref(null)

    const isComentarioDialogOpen = ref(false)
    const timelineComentarios = ref([])

    async function getComentarios() {
      const items = await TicketService.getAgenteAgendamentosComentarios(props.eventoSelecionado.ticketAgendamentoId)

      let left = true
      let right = false

      timelineComentarios.value = items.map(it => {
        left = !left
        right = !right

        return {
          id: it.id,
          pessoaNome: it.pessoaNome,
          comentario: it.comentario,
          dataCadastro: it.dataCadastro,
          left,
          right,
        }
      })
    }

    onMounted(getComentarios)

    watch(
      () => props.eventoSelecionado,
      v => {
        if (!v || !v.dataSolicitadaParaAgendamento || !v.dataFinalAgendamento) {
          dataAgendamento.value = null
          horarioAgendamento.value = null
          horarioFinalAgendamento.value = null

          return
        }
        const [data, horario] = v.dataSolicitadaParaAgendamento.split('T')

        dataAgendamento.value = data
        horarioAgendamento.value = getTimeStringComApenasHorasEMinutos(horario)

        const [, horario2] = v.dataFinalAgendamento.split('T')

        horarioFinalAgendamento.value = getTimeStringComApenasHorasEMinutos(horario2)
      },
      { immediate: true },
    )

    return {
      dataAgendamento,
      horarioAgendamento,
      horarioFinalAgendamento,
      isComentarioDialogOpen,
      timelineComentarios,
      closeDialog,
      getComentarios,
      icons: {
        mdiComment,
      },
    }
  },
}
</script>
